import React from 'react';
import { useSettings } from './context';
import { S } from '../../styles';
import { API, authHeaders, startLightspeedConnect, startStripeConnect } from '../../utils/helpers';
import SpinboardSubscription from './SpinboardSubscription';

// The Text Receipt extension's Chrome Web Store listing (Unlisted: installs by
// link only). Its id is also in server/index.js corsOptions.
export const EXTENSION_URL = 'https://chromewebstore.google.com/detail/cgljdemmibgecponejhabdbgnglilnki';

// Connections — extracted from Settings.jsx on 17 Sep 2026. The markup is
// unchanged; every value it needs comes from the Settings context rather than
// from props, so this move could not alter behaviour.
export default function SettingsConnections() {
  const { T, authStatus, createTerminal, currentUser, disconnectStripe, freshKey, isDark, keyCopied, lsDefaults, newTerminalLabel, revokeTerminal, setFreshKey, setKeyCopied, setLightspeed, setNewTerminalLabel, setShowConnAdvanced, setShowRevokedTerminals, setTerminalError, settings, showConnAdvanced, showRevokedTerminals, stripeBanner, stripeStatus, terminalBusy, terminalError, terminals, terminalsLoading } = useSettings();
  return (
    <>
      {/* Account / Connection */}
      <div style={{ ...S.settingsCard, background:T.card, border:`1px solid ${T.cardBorder}` }}>
        <div style={{ ...S.settingsCardTitle, color:T.text }}>Lightspeed Connection</div>
        {/* Three states, not two. `connectionBroken` = a token is stored but
            Lightspeed refuses it. Shown as its own state so nobody reads a
            green "Connected" over a queue that will not load. */}
        <div style={{ ...S.settingsCardDesc, color:T.textSub }}>
          {!authStatus?.authenticated
            ? 'Not connected. Connect your Lightspeed account to access live service queue data.'
            : authStatus?.connectionBroken
              ? 'Lightspeed is no longer accepting this connection, so tickets, customers and payments cannot load. Press Reconnect and sign in to Lightspeed — it keeps the same account and nothing in Spinboard is lost.'
              : 'Connected to Lightspeed R-Series and working. If tickets or customers ever stop loading, Reconnect re-authorises this same account.'}
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <div style={{ width:8, height:8, borderRadius:'50%', background: authStatus?.authenticated && !authStatus?.connectionBroken ? '#2ecc71' : '#e74c3c', flexShrink:0 }} />
          <span style={{ fontSize:13, color: authStatus?.authenticated && !authStatus?.connectionBroken ? '#2ecc71' : '#e74c3c', fontWeight:500 }}>
            {!authStatus?.authenticated ? 'Not connected' : authStatus?.connectionBroken ? 'Connection not working' : 'Connected'}
          </span>
          {authStatus?.authenticated
            ? <>
              {/* One visible action. Reconnect is the only one anyone routinely
                  needs — it repairs a stale connection, which is the failure
                  people actually hit — so it is the only one shown. Switching
                  accounts and severing the integration are rare, owner-level,
                  and destructive in different ways; they sit behind a
                  disclosure so they cannot be misread as the obvious next step
                  by someone whose queue just stopped loading.

                  Not owner-gated, and deliberately run WITHOUT
                  allowAccountChange: it can only ever restore the same account.
                  "Connected" means a token row exists, not that it works, so a
                  revoked token reads as healthy — whoever is on shift when that
                  happens has to be able to fix it, and at a bike shop on a
                  Saturday that is not the owner. */}
              <button style={authStatus?.connectionBroken
                  ? { ...S.btn(true), background:T.btnPrimary, color:T.btnPrimaryText, fontSize:12, marginLeft:'auto' }
                  : { ...S.btn(false), fontSize:12, color:T.textSub, marginLeft:'auto' }}
                onClick={() => startLightspeedConnect(false).catch(e => alert(e.message))}>
                Reconnect
              </button>
              {currentUser?.role === 'owner' && (
                <button style={{ background:'none', border:'none', padding:'4px 2px', cursor:'pointer',
                  fontSize:12, color:T.textSub, textDecoration:'underline', opacity:0.75 }}
                  onClick={() => setShowConnAdvanced(v => !v)}>
                  {showConnAdvanced ? 'Hide advanced' : 'Advanced'}
                </button>
              )}
            </>
                        : <button style={{ ...S.btn(true), background:T.btnPrimary, color:T.btnPrimaryText, fontSize:12, marginLeft:'auto' }}
                onClick={() => startLightspeedConnect().catch(e => alert(e.message))}>
                Connect Lightspeed
              </button>
          }
        </div>

        {authStatus?.authenticated && currentUser?.role === 'owner' && showConnAdvanced && (
          <div style={{ marginTop:14, paddingTop:14, borderTop:`1px solid ${T.cardBorder}`, display:'flex', flexWrap:'wrap', alignItems:'center', gap:12 }}>
            <button style={{ ...S.btn(false), fontSize:12, color:T.textSub }}
              onClick={() => {
                // Deliberately NOT disconnect-then-reconnect: that sequence
                // leaves the shop with no integration in between, so a failed
                // second step destroys a working connection to attempt a
                // replacement. This swaps atomically — it either completes or
                // nothing changes.
                if (!window.confirm("Connect a different Lightspeed account?\n\nSpinboard will move to whichever account you sign in as. Your current connection stays active until the new one succeeds.\n\nOnly do this if you are moving Spinboard to a different Lightspeed account — to fix a broken connection, use Reconnect.")) return;
                startLightspeedConnect(true).catch(e => alert(e.message));
              }}>
              Connect a different account
            </button>
            <button style={{ ...S.btn(false), fontSize:12, color:'#e74c3c', borderColor:'#e74c3c33' }}
              onClick={async () => {
                // Named "Disconnect Lightspeed", not "Sign out". This does not
                // sign a user out — it clears the shop's OAuth tokens, breaking
                // every Lightspeed-backed feature for everyone until someone
                // reconnects. The old label was the likeliest way this fired.
                if (!window.confirm("Disconnect Lightspeed?\n\nThis breaks the POS connection for the whole shop — tickets, customers, and payments stop working until someone reconnects. This does NOT sign you out of Spinboard.")) return;
                await fetch(`${API}/auth/disconnect`, { method:'POST', credentials:'include', headers: authHeaders() });
                window.location.reload();
              }}>
              Disconnect Lightspeed
            </button>
            <span style={{ fontSize:12, color:T.textSub, opacity:0.8 }}>
              Both affect the whole shop. Neither is needed to fix a connection that has stopped working — use Reconnect for that.
            </span>
          </div>
        )}
      </div>

      <div style={{ ...S.settingsCard, background:T.card, border:`1px solid ${T.cardBorder}` }}>
        <div style={{ ...S.settingsCardTitle, color:T.text }}>Lightspeed Defaults</div>
        <div style={{ ...S.settingsCardDesc, color:T.textSub }}>
          Which employee, register, and location Spinboard records online payments under in Lightspeed. These appear on the deposit sales Spinboard creates, the same way a sale rung up at the counter carries whoever rang it.
        </div>

        {[
          { key:'employeeId', label:'Employee',  list:lsDefaults.employees, empty:'No employees found' },
          { key:'registerId', label:'Register',  list:lsDefaults.registers, empty:'No registers found' },
          { key:'shopId',     label:'Location',  list:lsDefaults.shops,     empty:'No locations found' },
        ].map(f => (
          <div key={f.key} style={S.thresholdRow}>
            <span style={{ ...S.thresholdLabel, minWidth:200, color:T.text }}>{f.label}</span>
            <select value={(settings.lightspeed || {})[f.key] || ''}
              onChange={e => setLightspeed({ [f.key]: e.target.value || null })}
              style={{ flex:1, background:T.input, border:`1px solid ${T.inputBorder}`, borderRadius:6, padding:'7px 12px', color:T.text, fontSize:13, outline:'none' }}>
              <option value="">— Select {f.label.toLowerCase()} —</option>
              {f.list.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </div>
        ))}

        <div style={{ fontSize:11, color:T.textSub, marginBottom:14, fontStyle:'italic', paddingLeft:2 }}>
          ⓘ Pick the employee you want online payments attributed to — many shops create a dedicated one (e.g. "Online") so these don't land under a real person's sales numbers. Register and Location should match wherever you'd ring up a deposit by hand.
        </div>

        {(() => {
          const ls = settings.lightspeed || {};
          const missing = [
            !ls.employeeId && 'employee',
            !ls.registerId && 'register',
            !ls.shopId && 'location',
          ].filter(Boolean);
          if (!missing.length) return null;
          return (
            <div style={{ fontSize:11.5, color:'#e74c3c', fontWeight:600, paddingLeft:2 }}>
              ⚠ Not set ({missing.join(', ')}) — online payments will collect in Stripe but fail to record in Lightspeed until all three are chosen.
            </div>
          );
        })()}
      </div>

      <div style={{ ...S.settingsCard, background:T.card, border:`1px solid ${T.cardBorder}` }}>
        <div style={{ ...S.settingsCardTitle, color:T.text }}>Stripe Connect</div>
        <div style={{ ...S.settingsCardDesc, color:T.textSub }}>
          Connect your shop's Stripe account to accept booking deposits. Your shop is the merchant of record — Stripe handles your payouts and disputes directly, same as your other Stripe-connected tools.
        </div>
        {stripeBanner && (
          <div style={{ marginBottom:14, padding:'10px 14px', borderRadius:8, fontSize:13,
            background: stripeBanner.type === 'success' ? (isDark ? '#0d2218' : '#f0fdf4') : (isDark ? '#2a0f0f' : '#fde8e8'),
            color: stripeBanner.type === 'success' ? '#2ecc71' : '#e74c3c' }}>
            {stripeBanner.text}
          </div>
        )}
        <div style={{ display:'flex', alignItems:'center', gap:12 }}>
          <div style={{ width:8, height:8, borderRadius:'50%', flexShrink:0,
            background: stripeStatus === null ? T.textSub
              : (stripeStatus.connected && stripeStatus.verified && stripeStatus.chargesEnabled !== false) ? '#2ecc71' : '#e74c3c' }} />
          <span style={{ fontSize:13, fontWeight:500,
            color: stripeStatus === null ? T.textSub
              : (stripeStatus.connected && stripeStatus.verified && stripeStatus.chargesEnabled !== false) ? '#2ecc71' : '#e74c3c' }}>
            {stripeStatus === null ? 'Checking…' : stripeStatus.connected ? 'Connected' : 'Not connected'}
          </span>
          {stripeStatus?.accountId && (
            <span style={{ fontSize:12, color:T.textSub, fontFamily:"'IBM Plex Mono', monospace" }}>{stripeStatus.accountId}</span>
          )}
          {stripeStatus?.connected && !stripeStatus.verified && (
            <span style={{ fontSize:12, color:'#e74c3c' }}>— Stripe could not verify this account</span>
          )}
          {stripeStatus?.connected && stripeStatus.verified && stripeStatus.chargesEnabled === false && (
            <span style={{ fontSize:12, color:'#e74c3c' }}>
              — charges disabled{stripeStatus.disabledReason ? ` (${stripeStatus.disabledReason})` : ''}
            </span>
          )}
          {/* A stored accountId alone proves nothing — platform keys may have
              changed since (sandbox → live), or the shop may have deauthorized
              Spinboard from their own dashboard. /status verifies against
              Stripe, so a failed verification must offer Connect, not only
              Disconnect: otherwise a stale account can only be cleared by
              editing the database, which is exactly what happened at cutover. */}
          <div style={{ display:'flex', gap:8, marginLeft:'auto' }}>
            {/* Owner-only, matching Disconnect below and the route itself:
                connecting a Stripe account decides where this shop's money
                lands. Hidden rather than shown-and-refused, so a tech is not
                handed a button that only ever produces an error. */}
            {currentUser?.role === 'owner'
              && (!stripeStatus?.connected || !(stripeStatus.verified && stripeStatus.chargesEnabled !== false)) && (
              <button style={{ ...S.btn(true), background:T.btnPrimary, color:T.btnPrimaryText, fontSize:12 }}
                onClick={() => startStripeConnect().catch(e => alert(e.message))}>
                {stripeStatus?.connected ? 'Reconnect Stripe' : 'Connect Stripe'}
              </button>
            )}
            {currentUser?.role === 'owner' && stripeStatus?.connected && (
              <button style={{ ...S.btn(false), fontSize:12, color:'#e74c3c', borderColor:'#e74c3c33' }}
                onClick={disconnectStripe}>Disconnect</button>
            )}
          </div>
        </div>
      </div>

      {/* ── Register Terminals ── */}
      <div style={{ ...S.settingsCard, background:T.card, border:`1px solid ${T.cardBorder}` }}>
        <div style={{ ...S.settingsCardTitle, color:T.text }}>Register Terminals</div>
        <div style={{ ...S.settingsCardDesc, color:T.textSub }}>
          Each register that uses the Text Receipt button in Lightspeed requires its own terminal
          key. Generate a key below, name it so the register can be identified, and enter it in the
          Spinboard extension on that machine. A terminal key permits sending text receipts only and
          grants no other access to Spinboard. The key is displayed once, when it is generated.
          Safeguard it as you would a password: do not share it, email it, or store it where others
          can see it. If a key may have been exposed, or a register is replaced, revoke that key and
          generate a new one.
        </div>

        {/* Getting the extension onto a register (18 Oct). The store listing is
            Unlisted: it installs from this link and does not appear in search,
            so Settings is where an owner finds it. Steps as in
            docs/register-install.md, Route A. */}
        <div style={{ border:`1px solid ${T.cardBorder}`, borderRadius:8, padding:'14px 16px', marginBottom:16 }}>
          <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, flexWrap:'wrap', marginBottom:8 }}>
            <div style={{ fontSize:13, fontWeight:700, color:T.text }}>Set up a register</div>
            <a href={EXTENSION_URL} target="_blank" rel="noreferrer"
              style={{ ...S.btn(true), background:T.btnPrimary, color:T.btnPrimaryText, fontSize:12, textDecoration:'none' }}>
              Get the Chrome extension
            </a>
          </div>
          <ol style={{ margin:0, paddingLeft:18, fontSize:12, color:T.textSub, lineHeight:1.7 }}>
            <li>On the register computer, open Chrome (the profile you use for Lightspeed) and press <strong>Get the Chrome extension</strong>. Then press <strong>Add to Chrome</strong>, and <strong>Add extension</strong>.</li>
            <li>Click the puzzle piece at the top right of Chrome and pin <strong>Spinboard Text Receipt</strong>, so staff can find it.</li>
            <li>Below, type a name for this register (for example "Front counter") and press <strong>Generate key</strong>. Copy the key.</li>
            <li>Click the Spinboard icon in Chrome, paste the key and press <strong>Save key</strong>. It should say <strong>Connected</strong> with your shop's name.</li>
          </ol>
          <div style={{ fontSize:12, color:T.textSub, marginTop:8 }}>
            One key per register. Chrome only, on Windows or Mac.
          </div>
        </div>

        {freshKey && (
          <div style={{ background:'#F4622A11', border:'1px solid #F4622A', borderRadius:8, padding:'14px 16px', marginBottom:16 }}>
            <div style={{ fontSize:13, fontWeight:700, color:T.text, marginBottom:6 }}>
              Key for “{freshKey.label}” — copy it now
            </div>
            <div style={{ fontSize:12, color:T.textSub, marginBottom:10, lineHeight:1.5 }}>
              This is the only time it is shown. Spinboard stores a hash, not the key, so there is no
              way to look it up later. If it gets lost, revoke this terminal and generate another.
            </div>
            <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
              <code style={{ flex:'1 1 320px', background:T.input, border:`1px solid ${T.inputBorder}`,
                borderRadius:6, padding:'9px 12px', color:T.text, fontSize:13,
                fontFamily:'monospace', wordBreak:'break-all' }}>
                {freshKey.key}
              </code>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(freshKey.key)
                    .then(() => setKeyCopied(true))
                    .catch(() => setTerminalError('Could not reach the clipboard — select the key and copy it by hand.'));
                }}
                style={{ ...S.btn(true), background:T.btnPrimary, color:T.btnPrimaryText, flexShrink:0 }}>
                {keyCopied ? '✓ Copied' : 'Copy'}
              </button>
              <button onClick={() => { setFreshKey(null); setKeyCopied(false); }}
                style={{ ...S.btn(false), color:T.textSub, borderColor:T.border, flexShrink:0 }}>
                Done
              </button>
            </div>
          </div>
        )}

        {terminalError && (
          <div style={{ fontSize:12, color:'#e74c3c', marginBottom:12 }}>{terminalError}</div>
        )}

        {terminalsLoading ? (
          <div style={{ fontSize:13, color:T.textSub, marginBottom:12 }}>Loading…</div>
        ) : terminals.length === 0 ? (
          <div style={{ fontSize:13, color:T.textSub, marginBottom:12 }}>
            No terminals yet. Name your register below and generate its key.
          </div>
        ) : (
          (() => {
            const activeTerminals = terminals.filter(t => !t.revoked_at);
            const revokedTerminals = terminals.filter(t => !!t.revoked_at);
            const renderTerminal = (t) => {
              const revoked = !!t.revoked_at;
              const fmt = (d) => d ? new Date(d).toLocaleDateString(undefined, { month:'short', day:'numeric', year:'numeric' }) : null;
              return (
                <div key={t.id} style={{ display:'flex', alignItems:'center', gap:12, flexWrap:'wrap',
                  background:T.bg, border:`1px solid ${T.border}`, borderRadius:8, padding:'10px 14px',
                  opacity: revoked ? 0.55 : 1 }}>
                  <div style={{ flex:'1 1 200px', minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:600, color:T.text }}>
                      {t.label}{revoked && <span style={{ fontWeight:400, color:'#e74c3c' }}> — revoked {fmt(t.revoked_at)}</span>}
                    </div>
                    <div style={{ fontSize:11, color:T.textSub, fontFamily:'monospace', marginTop:2 }}>
                      {t.key_prefix}… · added {fmt(t.created_at)} · {t.last_used_at ? `last used ${fmt(t.last_used_at)}` : 'never used'}
                    </div>
                  </div>
                  {!revoked && (
                    <button onClick={() => revokeTerminal(t)}
                      style={{ ...S.btn(false), padding:'6px 12px', fontSize:12, color:'#e74c3c',
                        borderColor:'#e74c3c33', flexShrink:0 }}>
                      Revoke
                    </button>
                  )}
                </div>
              );
            };
            return (
              <div style={{ marginBottom:16 }}>
                {activeTerminals.length === 0 ? (
                  <div style={{ fontSize:13, color:T.textSub, marginBottom:8 }}>
                    No active terminals. Name your register below and generate its key.
                  </div>
                ) : (
                  <div style={{ display:'grid', gap:8 }}>
                    {activeTerminals.map(renderTerminal)}
                  </div>
                )}
                {revokedTerminals.length > 0 && (
                  <>
                    <button
                      id="toggle-revoked-terminals"
                      onClick={() => setShowRevokedTerminals(v => !v)}
                      aria-expanded={showRevokedTerminals}
                      style={{ background:'transparent', border:'none', padding:'10px 0 0', cursor:'pointer',
                        fontSize:12, color:T.textSub, fontFamily:'Arial, sans-serif' }}>
                      {showRevokedTerminals ? '▾ Hide' : '▸ Show'} {revokedTerminals.length} revoked {revokedTerminals.length === 1 ? 'key' : 'keys'}
                    </button>
                    {showRevokedTerminals && (
                      <div style={{ display:'grid', gap:8, marginTop:8 }}>
                        {revokedTerminals.map(renderTerminal)}
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })()
        )}

        <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
          <input
            value={newTerminalLabel}
            onChange={e => { setNewTerminalLabel(e.target.value); setTerminalError(null); }}
            onKeyDown={e => { if (e.key === 'Enter' && !terminalBusy) createTerminal(); }}
            placeholder="e.g. Front counter register"
            maxLength={80}
            style={{ flex:'1 1 240px', background:T.input, border:`1px solid ${T.inputBorder}`, borderRadius:6,
              padding:'8px 12px', color:T.text, fontSize:13, fontFamily:'Arial, sans-serif', outline:'none' }}
          />
          <button onClick={createTerminal} disabled={terminalBusy}
            style={{ ...S.btn(false), color:T.text, borderColor:T.border, flexShrink:0,
              opacity: terminalBusy ? 0.6 : 1, cursor: terminalBusy ? 'default' : 'pointer' }}>
            {terminalBusy ? 'Generating…' : 'Generate key'}
          </button>
        </div>
      </div>

      {currentUser?.role === 'owner' && <SpinboardSubscription T={T} />}
    </>
  );
}
